import React, { useState } from "react";
import { Play } from "lucide-react";

import { getChatVideoBubbleSize } from "@fitness-app/client-shared/features/chat/chat-media-bubble";
import { type FileMessageContent } from "@fitness-app/data-models/entities/Chat";
import { formatMediaDuration } from "@fitness-app/utils/src/media/formatMediaDuration";
import { cn } from "@fitness-app/utils/src/styles/cn";

import VideoPlayer from "~/components/VideoPlayer/VideoPlayer";

interface VideoAttachmentProps {
  /** Structural, so the list can pass either a sent attachment or one still uploading. */
  file: Pick<FileMessageContent, "originalName"> & {
    url?: string | null;
    thumbUrl?: string | null;
    duration?: number | null;
  };
  position?: "left" | "right";
}

/** Big enough for the play control to sit comfortably on a 240 px poster. */
const PLAY_BADGE_SIZE = 48;

/**
 * A lone video attachment, shown as a poster the size of a media bubble instead of the 56 px tile
 * (TRE-2117) — at tile size a video is barely identifiable. A message with several attachments
 * keeps the small strip, where these would not line up.
 */
export const VideoAttachment = ({ file, position }: VideoAttachmentProps): React.ReactElement => {
  const size = getChatVideoBubbleSize();
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("flex px-3 py-1", position === "right" ? "justify-end" : "justify-start")}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Odtwórz ${file.originalName}`}
        title={file.originalName}
        style={{ width: size, height: size, backgroundImage: file.thumbUrl ? `url(${file.thumbUrl})` : undefined }}
        // `bg-black/90` only shows through when there is no poster: a background colour paints
        // under backgroundImage, the same as the small tile.
        className="group relative max-w-full cursor-pointer overflow-hidden rounded-xl border-0 bg-black/90 bg-cover bg-center p-0 outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
      >
        <span
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 backdrop-blur-[2px] transition-[background-color,transform] group-hover:scale-105 group-hover:bg-black/65"
          style={{ width: PLAY_BADGE_SIZE, height: PLAY_BADGE_SIZE }}
        >
          <Play size={22} fill="currentColor" strokeWidth={0} className="ml-0.5 text-white" />
        </span>
        {file.duration ? (
          <span className="pointer-events-none absolute bottom-2 right-2 rounded bg-black/70 px-1.5 text-[11px] font-medium leading-5 text-white">
            {formatMediaDuration(file.duration)}
          </span>
        ) : null}
      </button>

      {file.url ? <VideoPlayer videoUrl={file.url} visible={open} onClose={() => setOpen(false)} /> : null}
    </div>
  );
};
